import { useEffect, useState } from "react";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";
import { onReplayEvent } from "../../utils/replayEvents";
import { replayStart, getReplayStatus } from "../../utils/replayControls";
import { replayCancelLoad } from "../../utils/viewMode";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Shown while a replay window loads. Opens on `replay-load-progress {active:true}`
 * (enter Replay, File>Load Data, or the toolbar prev/next), closes on
 * `{active:false}` (loaded / cancelled / error). `replay-load-error {message}`
 * switches it to an error state. Cancel issues `replay_cancel_load`. Mounted once.
 *
 * Mounted ONLY in the main window (App.tsx). The load is per-session and tool
 * windows (graph / diagnostics) share that session, so this instance also covers
 * a "Replay From" initiated in a tool window — the prompt/progress always appears
 * here in the main window, never duplicated in the tool window that started it.
 */
export function ReplayLoadDialog() {
  const [turbine, setTurbine] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // After a load completes the backend arms the replay paused and emits
  // `replay-data-loaded`; we then prompt to start it (C++ Ui_start_replay_dialog).
  const [propose, setPropose] = useState<string | null>(null);
  // "Starting replay…" (Start, data already loaded) vs "Loading replay data…".
  const [starting, setStarting] = useState(false);

  // Live load-progress + error are Tauri events the desktop backend emits.
  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];
    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      const offP = await listen<{ active: boolean; turbine: string; starting?: boolean }>("replay-load-progress", (e) => {
        if (e.payload.active) { setTurbine(e.payload.turbine); setStarting(!!e.payload.starting); setError(null); }
        else { setTurbine(null); }
      });
      const offE = await listen<{ message: string }>("replay-load-error", (e) => {
        setTurbine(null);
        setError(e.payload.message);
      });
      if (cancelled) { offP(); offE(); } else { offs.push(offP, offE); }
    });
    return () => { cancelled = true; offs.forEach((o) => o()); };
  }, []);

  // Web has no event channel, so poll get_replay_status and drive the same
  // "Loading replay data…" / "Starting replay…" progress the desktop gets from
  // replay-load-progress events. The replay-data-loaded subscription below then
  // shows the start prompt once the window finishes loading (full desktop parity:
  // pressing OK in Load Data now shows the progress dialog, then the start prompt).
  useEffect(() => {
    if (isTauri()) return;
    let stopped = false;
    const tick = async () => {
      const s = await getReplayStatus();
      if (stopped || !s) return;
      if (s.loading) {
        setTurbine(s.config ?? "");
        setStarting(!!s.starting);
        setError(null);
      } else {
        // Load finished/cancelled: close the progress dialog. The propose prompt
        // (replay-data-loaded poll) then opens for a freshly loaded window.
        setTurbine(null);
      }
    };
    void tick();
    const id = setInterval(tick, 1000);
    return () => { stopped = true; clearInterval(id); };
  }, []);

  // After a window finishes loading, the backend arms the replay paused; prompt to
  // start it (C++ Ui_start_replay_dialog). Works on desktop (wraps the Tauri event)
  // AND on the web -live tab (onReplayEvent polls get_replay_status and fires on a
  // freshly loaded window) — which is why pressing Load Data on the web now shows
  // this prompt instead of nothing.
  useEffect(() => {
    return onReplayEvent("replay-data-loaded", () => {
      setTurbine(null);
      setError(null);
      setPropose("loaded");
    });
  }, []);

  if (propose) {
    const onYes = async () => {
      try {
        await replayStart(); // Tauri invoke or web /api/replay_start
      } catch (e) {
        console.error("replay_start failed:", e);
      }
      setPropose(null);
    };
    // No: leave the replay loaded but paused (the toolbar Play can start it).
    return (
      <div style={styles.overlay}>
        <div style={styles.dialog}>
          <div style={styles.header}>
            <span style={styles.title}>{getTigerResource("TIGER_DATA_LOADED_TITLE")}</span>
          </div>
          <div style={styles.body}>
            <div style={styles.label}>
              {getTigerResource("TIGER_DATA_LOADED_MSG")}
            </div>
            <div style={styles.buttons}>
              <button className="btn-acrylic-secondary" onClick={() => setPropose(null)}>
                {getTigerResource("TIGER_RES_NO")}
              </button>
              <button className="btn-acrylic-primary" onClick={onYes}>
                {getTigerResource("TIGER_RES_YES")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!turbine && !error) return null;

  const onCancel = async () => {
    try {
      await replayCancelLoad(); // Tauri invoke or web /api/replay_cancel_load
    } catch (e) {
      console.error("replay_cancel_load failed:", e);
    }
    // Close optimistically regardless; the backend also emits progress{active:false}.
    setTurbine(null);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <span style={styles.title}>
            {turbine ? tigerFormat(getTigerResource("TIGER_REPLAY_TITLE"), turbine) : getTigerResource("TIGER_RES_REPLAY")}
          </span>
        </div>
        <div style={styles.body}>
          {error ? (
            <>
              <div style={styles.errLabel}>{error}</div>
              <div style={styles.buttons}>
                <button className="btn-acrylic-primary" onClick={() => setError(null)}>{getTigerResource("TIGER_RES_DISMISS")}</button>
              </div>
            </>
          ) : (
            <>
              <div style={styles.label}>{starting ? getTigerResource("TIGER_REPLAY_STARTING") : getTigerResource("TIGER_REPLAY_LOADING")}</div>
              <div style={styles.barOuter}><div style={styles.barIndet} /></div>
              <div style={styles.buttons}>
                <button className="btn-acrylic-secondary" onClick={onCancel}>
                  {getTigerResource("TIGER_RES_CANCEL")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  overlay: { ...acrylicStyles.overlay, display: "flex", alignItems: "center", justifyContent: "center" },
  dialog: {
    background: "var(--ui-surface)", backdropFilter: "var(--ui-surface-blur)", WebkitBackdropFilter: "var(--ui-surface-blur)",
    border: "1px solid rgba(255,255,255,0.65)", borderRadius: 14, width: 380, maxWidth: "95vw",
    boxShadow: "0 16px 48px rgba(20,30,60,0.35)", overflow: "hidden" as const,
    display: "flex", flexDirection: "column" as const, fontFamily: acrylicTokens.font, color: acrylicTokens.textPrimary,
  },
  errLabel: { color: "#c92a2a", fontSize: 13 },
  barOuter: { position: "relative" as const, height: 8, background: "rgba(255,255,255,0.85)", border: "1px solid rgba(120,130,150,0.35)", borderRadius: 6, overflow: "hidden" },
  barIndet: { position: "absolute" as const, left: 0, top: 0, bottom: 0, width: "40%", background: "var(--ui-btn-primary-bg)", animation: "tiger-indet 1.2s ease-in-out infinite" },
  buttons: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 },
} as const;
